import type { Card, PriceHistoryEntry } from "@/context/AppContext";
import { apiFetch } from "@/lib/apiClient";

export interface EbaySale {
  price: number;
  /** ISO string from eBay's listing endTime. May be empty if missing. */
  soldAt: string;
}

interface EbaySoldPriceResponse {
  price: number | null;
  count: number;
  sales?: EbaySale[];
}

// Module-level cache so rapid Refresh clicks (or the bulk-refresh worker
// hitting the same card name twice in a session) don't hammer eBay's Finding
// API. Keyed by the exact query string we send; 5-minute TTL matches what's
// reasonable for "recent sales" volatility on most cards.
const CACHE_TTL_MS = 5 * 60 * 1000;
interface CachedFetch {
  expires: number;
  data: { median: number | null; sales: EbaySale[] };
}
const cache = new Map<string, CachedFetch>();
const inflight = new Map<
  string,
  Promise<{ median: number | null; sales: EbaySale[] }>
>();

/**
 * Fetches the 10 most recent eBay sold listings for a card name. Returns
 * `{ median, sales }` where `sales` is the per-listing array (newest first
 * per eBay's EndTimeSoonest sort). `median` is null when there are no comps.
 *
 * Hard 6s timeout. eBay's Finding API (especially the sandbox) can sit on
 * a request for tens of seconds before responding; we'd rather degrade to
 * "no sales appended this refresh" than make the user wait. Times-out
 * count as a soft failure — the caller should fall through to the empty
 * sales array, not block.
 *
 * Results are cached per-query for 5 minutes; concurrent calls for the same
 * query share a single inflight Promise instead of firing parallel requests.
 */
export async function fetchEbaySales(
  card: Pick<Card, "name" | "setName">,
  { timeoutMs = 6000 }: { timeoutMs?: number } = {},
): Promise<{ median: number | null; sales: EbaySale[] }> {
  const q = [card.name, card.setName].filter(Boolean).join(" ");
  if (!q.trim()) return { median: null, sales: [] };

  const cached = cache.get(q);
  if (cached && cached.expires > Date.now()) return cached.data;
  const existing = inflight.get(q);
  if (existing) return existing;

  const p = (async () => {
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), timeoutMs);
    try {
      const res = await apiFetch(
        `/api/ebay/sold-price?q=${encodeURIComponent(q)}&categoryId=183050&details=true`,
        { signal: ctrl.signal },
      );
      if (!res.ok) throw new Error(`eBay sold-price failed (${res.status})`);
      const data = (await res.json()) as EbaySoldPriceResponse;
      const sales = (data.sales ?? []).filter(
        (s) => Number.isFinite(s.price) && s.price > 0,
      );
      const result = { median: data.price ?? null, sales };
      cache.set(q, { expires: Date.now() + CACHE_TTL_MS, data: result });
      return result;
    } finally {
      clearTimeout(timer);
      inflight.delete(q);
    }
  })();
  inflight.set(q, p);
  return p;
}

/**
 * Stable de-dup key for a price-history entry. Day-precision + price-cents +
 * source so the same eBay listing showing up across multiple refreshes doesn't
 * stack. Manual entries dedupe by exact ISO timestamp + price so users can log
 * the same price twice on the same day if they intend to (different sales).
 */
function entryKey(e: PriceHistoryEntry): string {
  const day = e.recordedAt.split("T")[0] ?? e.recordedAt;
  if (e.source === "Manual" || e.source === "Other") {
    return `${e.recordedAt}-${e.price.toFixed(2)}-${e.source}`;
  }
  return `${day}-${e.price.toFixed(2)}-${e.source}`;
}

/**
 * Merge fresh eBay sales into an existing priceHistory, deduping against
 * what's already there. Newest entries first. Caps the result at `cap` (default
 * 20) so old entries fall off as new comps arrive.
 */
export function mergeEbaySalesIntoHistory(
  existing: PriceHistoryEntry[],
  sales: EbaySale[],
  cap = 20,
): PriceHistoryEntry[] {
  const seen = new Set(existing.map(entryKey));
  const incoming: PriceHistoryEntry[] = [];
  for (const s of sales) {
    if (!s.soldAt) continue;
    const entry: PriceHistoryEntry = {
      price: s.price,
      source: "eBay Sold",
      recordedAt: s.soldAt,
    };
    const key = entryKey(entry);
    if (seen.has(key)) continue;
    seen.add(key);
    incoming.push(entry);
  }
  const combined = [...existing, ...incoming];
  combined.sort((a, b) => b.recordedAt.localeCompare(a.recordedAt));
  return combined.slice(0, cap);
}
